// Core imports
import * as React from 'react';
import remove from 'lodash.remove';
import { emailValidation } from 'src/utils/inputValidation';
import invoiceServiceInstance from 'src/services/invoice/InvoiceService';

// Components imports
import ChipsInput from 'src/components/common/ChipsInput';
import Button from 'src/components/common/Button';
import EmailInvoiceCard from '../EmailInvoiceCard';

// Models imports
import InvoiceModel from 'src/model/invoice.model';

// Style and misc imports
import './EmailInvoicesModal.scss';
import Alert from 'src/components/common/Alert';

class EmailInvoicesModal extends React.Component<
  IEmailInvoicesModalProps,
  IEmailInvoicesModalState
> {
  constructor(props: IEmailInvoicesModalProps) {
    super(props);

    this.state = {
      emailAddresses: [],
      emailError: false,
      sendEmailSuccessful: false,
      sendEmailError: false
    };
  }

  /* 
    setEmailAddresses
   */
  private setEmailAddresses = (address: string) => {
    const { emailAddresses } = this.state;
    if (emailValidation(address)) {
      this.setState({
        emailAddresses: [address, ...emailAddresses],
        emailError: false
      });
    } else {
      this.setState({ emailError: true });
    }
  };

  /* 
    removeEmail
   */
  private removeEmail = (email: string) => {
    const newEmailAddresses = [...this.state.emailAddresses];
    remove(newEmailAddresses, (emailAddress: string) => email === emailAddress);
    this.setState({ emailAddresses: newEmailAddresses });
  };

  /* 
    getInvoicesToPrint
   */
  private getInvoicesToPrint = (): JSX.Element[] => {
    const invoicesToPrint: JSX.Element[] = [];
    const { selectedInvoices } = this.props;
    selectedInvoices.map((invoice, index) => {
      invoicesToPrint.push(
        <EmailInvoiceCard
          key={index}
          invoice={invoice}
          removeInvoiceFromSelected={this.props.removeInvoiceFromSelected}
        />
      );
    });
    return invoicesToPrint;
  };

  /* 
    prepareInvoicesNumbers
   */
  private prepareInvoicesNumbers = () => {
    const { selectedInvoices } = this.props;
    const invoicesNumbers: any[] = [];
    selectedInvoices.map(invoices => {
      invoicesNumbers.push(
        `${invoices.invoiceNumber.toString()}:${invoices.invoiceDate.toString()}`
      );
    });
    return invoicesNumbers;
  };

  private closeEmailAlert = () => {
    this.setState({ sendEmailError: false });
  };

  /*
  sendEmailService
  */

  private sendEmailService = () => {
    if (this.props.selectedInvoices.length !== 0) {
      invoiceServiceInstance
        .sendInvoiceByEmail(
          this.state.emailAddresses,
          this.prepareInvoicesNumbers()
        )
        .then(response => {
          this.setState({ sendEmailSuccessful: true, sendEmailError: false });
        })
        .catch(error => {
          this.setState({ sendEmailSuccessful: false, sendEmailError: true });
        });
    }
  };

  /* 
    sendEmail
   */
  private sendEmail = () => {
    const addresses: string[] = [];
    this.state.emailAddresses.map(mail => {
      const newMstr = mail.replace(/\s/g, '');
      addresses.push(newMstr);
    });
    this.setState({ emailAddresses: addresses }, () => this.sendEmailService());
  };

  /* 
    getDesktopButtons
   */
  private getDesktopButtons = () => {
    const { t, closeModal } = this.props;
    if (!this.props.isMobile) {
      if (!this.state.sendEmailSuccessful) {
        return (
          <React.Fragment>
            <Button
              buttonClass="secondary-custom-button send-invoices-button"
              textTransform="uppercase"
              label={t('common.cancel')}
              action={() => {
                closeModal();
              }}
              height="38px"
              width="158px"
            />
            <Button
              buttonClass="primary-custom-button send-invoices-button"
              textTransform="uppercase"
              label={t('common.sendEmail')}
              action={() => {
                this.sendEmail();
              }}
              height="38px"
              width="158px"
              disabled={this.state.emailAddresses.length === 0}
            />
          </React.Fragment>
        );
      } else {
        return (
          <React.Fragment>
            <Button
              buttonClass="primary-custom-button send-invoices-button"
              textTransform="uppercase"
              label={t('invoices.goBackToInvoices')}
              action={() => {
                closeModal();
              }}
              height="38px"
              width="238px"
              disabled={this.state.emailAddresses.length === 0}
            />
          </React.Fragment>
        );
      }
    } else {
      return <React.Fragment />;
    }
  };

  /* 
    getMobileButtons
   */
  private getMobileButtons = () => {
    const { t, closeModal } = this.props;
    if (!this.state.sendEmailSuccessful) {
      return (
        <div>
          <Button
            buttonClass="primary-custom-button send-invoices-button-mobile"
            textTransform="uppercase"
            label={t('common.sendEmail')}
            action={() => {
              this.sendEmail();
            }}
            height="38px"
            width="100%"
            disabled={this.state.emailAddresses.length === 0}
          />
        </div>
      );
    } else {
      return (
        <Button
          buttonClass="primary-custom-button send-invoices-button-mobile"
          textTransform="uppercase"
          label={t('invoices.goBackToInvoices')}
          action={() => {
            closeModal();
          }}
          height="38px"
          width="100%"
          disabled={this.state.emailAddresses.length === 0}
        />
      );
    }
  };

  /* 
    getEmailComponent
   */
  private getEmailComponent = () => {
    const { t } = this.props;
    const { emailError } = this.state;
    if (!this.state.sendEmailSuccessful) {
      return (
        <ChipsInput
          chips={this.state.emailAddresses}
          onAddChip={this.setEmailAddresses}
          containerClassname="email-container"
          inputClassname="email-input"
          chipClassName="chip"
          max={3}
          placeholder={t('formData.enterEmail_plural')}
          clearOnAdd={true}
          onRemoveChip={this.removeEmail}
          errorState={emailError}
        />
      );
    } else {
      return (
        <div className="email-address-container">
          {this.state.emailAddresses.map((email, index) => {
            return (
              <span key={index} className="email-address">
                {email}
              </span>
            );
          })}
        </div>
      );
    }
  };

  public render() {
    const { t, selectedInvoices } = this.props;
    return (
      <div className="rp-invoice-email-modal">
        {this.state.sendEmailSuccessful ? (
          <div className="rp-invoice-email-modal--header-success">
            <span className="rpds-check-circle success-icon" />
            <div className="text">
              <h1 className="title">
                {selectedInvoices.length > 1
                  ? t('invoices.invoiceSent_plural')
                  : t('invoices.invoiceSent')}
              </h1>
              <span className="legend">
                {selectedInvoices.length > 1
                  ? t('invoices.sentInvoiceToEmailWithCount_plural', {
                      count: selectedInvoices.length
                    })
                  : t('invoices.sentInvoiceToEmailWithCount', {
                      count: selectedInvoices.length
                    })}
              </span>
            </div>
          </div>
        ) : (
          <React.Fragment>
            {this.state.sendEmailError && (
              <Alert
                styleClass="rp-email-error-alert"
                type={'error'}
                icon={'rpds-error'}
                message={
                  "We couldn't send your invoices, check all the emails are correct or try again later."
                }
                handleCloseAlert={this.closeEmailAlert}
              />
            )}
            <div className="rp-invoice-email-modal--header">
              <h1 className="title">{t('formData.enterEmail_plural')}</h1>
              <span className="legend">
                {t('invoices.sendInvoiceToEmailWithCount', {
                  count: selectedInvoices.length
                })}
              </span>
            </div>
          </React.Fragment>
        )}
        <div className="rp-invoice-email-modal--email-select">
          {!this.state.sendEmailSuccessful && (
            <span className="title">{t('formData.emailAddress')}</span>
          )}
          {this.getEmailComponent()}
          {!this.state.sendEmailSuccessful && (
            <span className="info">Press the enter key to finalize email.</span>
          )}
        </div>
        <div className="rp-invoice-email-modal--desktop-buttons">
          {this.getDesktopButtons()}
        </div>
        {this.getMobileButtons()}
        <div className="rp-invoice-email-modal--invoices">
          <div className="rp-invoice-email-modal--invoices--headers">
            <span className="header--invoice-number header-column">
              {t('invoices.invoiceNumberSymbol')}
            </span>
            <span className="header--invoice-date header-column">
              {t('invoices.filters.invoiceDate')}
            </span>
            <span className="header--temaining-amount header-column">
              {t('invoices.filters.remainingAmount')}
            </span>
            <span className="header--due-date header-column">
              {t('invoices.filters.dueDate')}
            </span>
            <span className="header--status header-column">
              {t('invoices.filters.status')}
            </span>
            <span className="header--empty" />
          </div>
          {this.getInvoicesToPrint()}
        </div>
      </div>
    );
  }
}

export default EmailInvoicesModal;

interface IEmailInvoicesModalProps {
  removeInvoiceFromSelected(invoice: InvoiceModel): void;
  closeModal(): void;
  t: any;
  selectedInvoices: InvoiceModel[];
  isMobile?: boolean;
}

interface IEmailInvoicesModalState {
  emailAddresses: string[];
  emailError: boolean;
  sendEmailSuccessful: boolean;
  sendEmailError: boolean;
}
